'use client'

import React from 'react'

import { BlogArticleCard, getBlogArticleCardProps } from '@/components/Blog/BlogArticleCard'
import { containerClassName } from '@/components/Container'
import { PageHero } from '@/components/Hero/PageHero'
import { useMessages } from '@/lib/i18n/useMessages'
import { useArchiveItemHref } from '@/lib/pages/LocalizedPagePathsProvider'
import type { BlogPostData } from '@/lib/blog/postCard'
import { cn } from '@/utilities/ui'

export type { BlogPostData } from '@/lib/blog/postCard'

const BLOG_EYEBROW_ICON = '/media/homepage/blog/blog-eyebrow-icon.svg'
const BLOG_HERO_BACKGROUND = '/media/homepage/blog/image%207.png'
const BLOG_BIRD_IMAGE = '/media/homepage/blog/blog-bird.png'

type BlogHeroSectionProps = {
  featuredPost?: BlogPostData | null
  headingId?: string
  headingLevel?: 'h1' | 'h2'
}

export const BlogHeroSection: React.FC<BlogHeroSectionProps> = ({
  featuredPost,
  headingId = 'blog-section-heading',
  headingLevel = 'h2',
}) => {
  const messages = useMessages()
  const copy = messages.landing.blog
  const Heading = headingLevel
  const featuredHref = useArchiveItemHref('posts', featuredPost?.slug ?? '')

  return (
    <PageHero
      aria-labelledby={headingId}
      as="section"
      backdropClassName="bg-bluetext"
      background={
        <img
          alt=""
          className="absolute inset-0 size-full object-cover object-bottom opacity-20 mix-blend-luminosity"
          src={BLOG_HERO_BACKGROUND}
        />
      }
      className="overflow-hidden"
      contentClassName="relative items-start justify-start py-0 pt-28 pb-20 lg:pt-36 lg:pb-24"
      contentWrapperClassName={cn(containerClassName, 'w-full items-start py-0 lg:py-0')}
      gradientClassName="hero-overlay-image-wash"
      pullUnderHeader={false}
    >
      <img
        alt=""
        aria-hidden
        className="pointer-events-none absolute -bottom-6 left-0 z-0 hidden h-auto w-44 -scale-x-100 lg:block lg:w-56 xl:w-72"
        height={418}
        src={BLOG_BIRD_IMAGE}
        width={418}
      />

      <div
        className={cn(
          'relative z-10 grid w-full gap-10',
          featuredPost &&
            'lg:grid-cols-[minmax(0,1fr)_32.5rem] lg:items-center lg:gap-12 xl:gap-20',
        )}
      >
        <div className="max-w-[40rem] self-start">
          <p className="flex items-center gap-1 font-outfit text-base font-medium text-white">
            <img
              alt=""
              aria-hidden
              className="size-4 shrink-0"
              height={16}
              src={BLOG_EYEBROW_ICON}
              width={16}
            />
            {copy.eyebrow}
          </p>

          <Heading
            className="mt-6 text-balance font-display text-[2.5rem] font-semibold leading-none tracking-[-0.04em] text-white drop-shadow-[0_16px_32px_-8px_rgba(12,12,13,0.4)] md:text-[3.5rem] lg:text-[4rem] lg:leading-[4rem]"
            id={headingId}
          >
            <span className="block">{copy.titleLine1}</span>
            <span className="block text-turquoise">{copy.titleLine2}</span>
          </Heading>

          <p className="mt-6 max-w-[30.75rem] font-outfit text-base leading-7 text-white/95">
            {copy.description}
          </p>
        </div>

        {featuredPost && featuredHref ? (
          <BlogArticleCard
            {...getBlogArticleCardProps(featuredPost, featuredHref, copy.readTime)}
            ctaLabel={copy.readMore}
            listItem={false}
            variant="featured"
          />
        ) : null}
      </div>
    </PageHero>
  )
}
